import React, { useState } from "react";
import { LayoutDashboard, User, LogOut, PlayCircle } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { StoreState } from "../../models/reduxModels";
import { LogOutAction } from "../../stores/actions/userAction";
import { isIPhoneDevice } from "../../utility/deviceDetector";

import IPhoneDashboard from "./IPhoneDashboard";
import IPhoneUserProfile from "./IPhoneUserProfile";
import IPhoneAddToHomeScreenModal, { checkIsStandalone } from "./IPhoneAddToHomeScreenModal";
import IPhoneInstructionVideoModal from "./IPhoneInstructionVideoModal";

type ActiveTab = "dashboard" | "profile";

const IPhoneLayout: React.FC = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: StoreState) => state.user.userDetails);
  const iphoneMode = useSelector((state: StoreState) => state.user.iphoneMode);
  const employeeDetail = userDetails?.employee_detail;

  const [activeTab, setActiveTab] = useState<ActiveTab>("dashboard");
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const isIPhone = isIPhoneDevice();
  const isStandalone = checkIsStandalone();
  const hasPrompted = typeof window !== "undefined" && localStorage.getItem("iaims_a2hs_prompted") === "true";

  // Video Guide Modal Auto-Popup Guard:
  // Show automatically on first-time landing in Attendance mode, then persist dismissal in localStorage
  const hasVideoGuideShown =
    typeof window !== "undefined" &&
    localStorage.getItem("iaims_iphone_video_guide_shown") === "true";
  const [showVideoModal, setShowVideoModal] = useState<boolean>(!hasVideoGuideShown);

  // A2HS Modal state: If video guide was already shown previously, check if A2HS should show; otherwise wait until video guide is closed
  const [showA2HSModal, setShowA2HSModal] = useState<boolean>(
    hasVideoGuideShown && isIPhone && iphoneMode === "attendance" && !isStandalone && !hasPrompted
  );

  const handleCloseVideoModal = () => {
    try {
      localStorage.setItem("iaims_iphone_video_guide_shown", "true");
    } catch (e) {}
    setShowVideoModal(false);

    // Sequential Trigger: After closing Attendance Guide modal, if PWA is NOT installed on iPhone, show PWA Install Modal
    if (isIPhone && iphoneMode === "attendance" && !checkIsStandalone() && !hasPrompted) {
      setTimeout(() => {
        setShowA2HSModal(true);
      }, 300);
    }
  };

  const handleConfirmLogout = () => {
    dispatch(LogOutAction() as any);
  };

  const profileImageUrl =
    userDetails?.imageSingUrl ||
    (employeeDetail as any)?.imageSingUrl ||
    (employeeDetail as any)?.employee_image ||
    "";
  const [headerImgError, setHeaderImgError] = useState<boolean>(false);

  return (
    <div className="flex flex-col min-h-[100dvh] w-full bg-gray-50 font-sans selection:bg-[#FF6B1A] selection:text-white">
      {/* Top Header Bar with iOS Safe-Area Support */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100 px-3 sm:px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2 min-h-[56px] flex items-center justify-between shadow-xs">
        <div className="flex items-center space-x-2.5 min-w-0">
          <div className="w-8 h-8 rounded-full bg-[#FFF3EB] border border-[#FF6B1A] flex items-center justify-center text-[#FF6B1A] font-bold text-xs shrink-0 overflow-hidden shadow-xs">
            {profileImageUrl && !headerImgError ? (
              <img
                src={profileImageUrl}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={() => setHeaderImgError(true)}
              />
            ) : employeeDetail?.employee_name ? (
              employeeDetail.employee_name.charAt(0).toUpperCase()
            ) : (
              <User className="w-4.5 h-4.5" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-xs font-bold text-gray-900 leading-tight truncate">
              {employeeDetail?.employee_name || userDetails?.userName || "User"}
            </h1>
            <p className="text-[10px] text-gray-500 truncate max-w-[120px] sm:max-w-[180px]">
              {userDetails?.designation_name || "Employee"}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1.5 shrink-0">
          {/* Eye-catching Animated Video Guide Trigger Button */}
          <button
            onClick={() => setShowVideoModal(true)}
            className="relative flex items-center space-x-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#EA580C] text-white text-[11px] font-bold shadow-md hover:shadow-lg transition-all active:scale-95 shrink-0"
            title="Watch Attendance Video Guide"
          >
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 animate-ping" />
            <PlayCircle className="w-3.5 h-3.5 fill-current text-white animate-pulse" />
            <span>Guide</span>
          </button>

          <button
            onClick={() => setShowLogoutModal(true)}
            className="p-1.5 rounded-xl text-gray-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
            title="Sign Out"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-md mx-auto px-3 sm:px-4 pt-3 pb-24">
        {activeTab === "dashboard" && <IPhoneDashboard />}
        {activeTab === "profile" && <IPhoneUserProfile />}
      </main>

      {/* Bottom Mobile Tab Bar with iOS Safe-Area Home Indicator Support */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-lg pb-[max(0.25rem,env(safe-area-inset-bottom))]">
        <div className="max-w-md mx-auto h-16 px-4 sm:px-8 flex items-center justify-around">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`flex flex-col items-center justify-center w-16 py-1 transition-all ${
              activeTab === "dashboard"
                ? "text-[#FF6B1A] font-bold"
                : "text-gray-400 hover:text-gray-600 font-medium"
            }`}
          >
            <LayoutDashboard className={`w-5 h-5 mb-0.5 ${activeTab === "dashboard" ? "scale-110" : ""}`} />
            <span className="text-[10px]">Dashboard</span>
          </button>

          <button
            onClick={() => setActiveTab("profile")}
            className={`flex flex-col items-center justify-center w-16 py-1 transition-all ${
              activeTab === "profile"
                ? "text-[#FF6B1A] font-bold"
                : "text-gray-400 hover:text-gray-600 font-medium"
            }`}
          >
            <User className={`w-5 h-5 mb-0.5 ${activeTab === "profile" ? "scale-110" : ""}`} />
            <span className="text-[10px]">Profile</span>
          </button>
        </div>
      </nav>

      {/* Instruction Video Modal Overlay */}
      <IPhoneInstructionVideoModal
        open={showVideoModal}
        onClose={handleCloseVideoModal}
      />

      {/* Logout Confirm Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="w-full max-w-xs bg-white rounded-2xl p-5 text-center shadow-xl border border-gray-100">
            <h3 className="text-base font-bold text-gray-900 mb-1">Log Out</h3>
            <p className="text-xs text-gray-500 mb-5">
              Are you sure you want to sign out of the portal?
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="py-2.5 px-3 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                className="py-2.5 px-3 rounded-xl bg-[#FF6B1A] hover:bg-[#EA580C] text-xs font-semibold text-white shadow-sm"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Home Screen Modal */}
      <IPhoneAddToHomeScreenModal
        open={showA2HSModal}
        onClose={() => setShowA2HSModal(false)}
      />
    </div>
  );
};

export default IPhoneLayout;

